import React, { FC, useState, useEffect } from 'react';
import FieldsRow from 'shared/components/FieldsRow';
import {
  UseFieldArrayRemove,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  BusinessTravelData,
} from 'models/ActivityDataEntryScopes/businessTravel.model';
import {
  getDistanceBasedFieldsData,
  getSpendBasedFieldsData,
  getHotelStayFieldsData,
} from './category6LayoutData';
import LocationModal, { generateAddressLabel } from '../Category4&9/LocationModal';
import { Address } from 'models/Address/address.model';
import { fetchGeocodeData } from 'shared/utils/fetchGeoLocation';
import fetchAddressFromGeoCode from 'shared/utils/fetchAddressFromGeoCode';

export interface Category6LayoutProps {
  index: number;
  type: 'distance' | 'spend' | 'hotel';
  remove?: UseFieldArrayRemove;
  setActiveTabKey?: React.Dispatch<React.SetStateAction<string | string[]>>;
}

const Category6Layout: FC<Category6LayoutProps> = ({
  index,
  type,
  remove,
  setActiveTabKey,
}) => {
  const { control, trigger, setValue, watch } = useFormContext<BusinessTravelData>();
  const [openOriginModal, setOpenOriginModal] = useState(false);
  const [openDestinationModal, setOpenDestinationModal] = useState(false);
  const [openSpendLocationModal, setOpenSpendLocationModal] = useState(false);
  const [openHotelLocationModal, setOpenHotelLocationModal] = useState(false);

  // Watch all form arrays at the top level (React Hooks rules)
  const distanceBasedTravel = useWatch({
    control,
    name: 'distanceBasedTravel',
  });

  const spendBasedTravel = useWatch({
    control,
    name: 'spendBasedTravel',
  });

  const hotelStays = useWatch({
    control,
    name: 'hotelStays',
  });

  const handleChange = async () => await trigger?.();

  // Watch travel mode for distance-based travel to set defaults and clear fields
  const currentTravelMode = type === 'distance'
    ? watch(`distanceBasedTravel.${index}.travelMode`)
    : null;

  // Watch current values of car and flight fields to avoid overwriting existing values
  const currentCarSize = type === 'distance'
    ? watch(`distanceBasedTravel.${index}.carSize`)
    : null;
  const currentCarType = type === 'distance'
    ? watch(`distanceBasedTravel.${index}.carType`)
    : null;
  const currentFlightClass = type === 'distance'
    ? watch(`distanceBasedTravel.${index}.flightClass`)
    : null;

  // Track previous travel mode to detect user-initiated changes vs. initial load
  const previousTravelModeRef = React.useRef<string | null | undefined>(undefined);

  // Set defaults and clear fields based on travel mode
  // Only set defaults when:
  // 1. User manually changes travel mode (not on initial form load from API)
  // 2. The relevant fields don't already have values
  useEffect(() => {
    if (type === 'distance' && currentTravelMode) {
      const previousTravelMode = previousTravelModeRef.current;
      const isInitialLoad = previousTravelMode === undefined;
      const travelModeChanged = previousTravelMode !== currentTravelMode;

      // Update ref for next render
      previousTravelModeRef.current = currentTravelMode;

      // Skip setting defaults on initial load - respect values from API
      if (isInitialLoad) {
        return;
      }

      // Only apply defaults/clearing when user actively changes travel mode
      if (travelModeChanged) {
        if (currentTravelMode === 'car') {
          // Set defaults for car travel only if fields are empty/null
          if (!currentCarSize) {
            setValue(`distanceBasedTravel.${index}.carSize`, 'average', { shouldValidate: false });
          }
          if (!currentCarType) {
            setValue(`distanceBasedTravel.${index}.carType`, 'average', { shouldValidate: false });
          }
          // Clear flight class when switching to car
          setValue(`distanceBasedTravel.${index}.flightClass`, null, { shouldValidate: false });
        } else if (currentTravelMode === 'air') {
          // Set default for air travel only if field is empty/null
          if (!currentFlightClass) {
            setValue(`distanceBasedTravel.${index}.flightClass`, 'average', { shouldValidate: false });
          }
          // Clear car fields when switching to air
          setValue(`distanceBasedTravel.${index}.carSize`, null, { shouldValidate: false });
          setValue(`distanceBasedTravel.${index}.carType`, null, { shouldValidate: false });
        } else {
          // Clear both car and flight fields for rail or other modes
          setValue(`distanceBasedTravel.${index}.carSize`, null, { shouldValidate: false });
          setValue(`distanceBasedTravel.${index}.carType`, null, { shouldValidate: false });
          setValue(`distanceBasedTravel.${index}.flightClass`, null, { shouldValidate: false });
        }
      }
    }
  }, [type, index, currentTravelMode, currentCarSize, currentCarType, currentFlightClass, setValue]);

  // Set default for number of passengers when entry is created
  useEffect(() => {
    if (type === 'distance') {
      const currentPassengers = watch(`distanceBasedTravel.${index}.numberOfPassengers`);
      if (currentPassengers === null || currentPassengers === undefined) {
        setValue(`distanceBasedTravel.${index}.numberOfPassengers`, 1, { shouldValidate: false });
      }
    }
  }, [type, index, setValue, watch]);

  // Helper function to geocode string address and create Address object
  // This makes location handling flexible - works with both string addresses (from Excel) and Address objects (from modal)
  const geocodeStringAddress = async (addressString: string | null | undefined): Promise<Address | undefined> => {
    if (!addressString || typeof addressString !== 'string' || addressString.trim() === '') {
      return undefined;
    }

    const trimmedAddress = addressString.trim();

    // If geocoding fails, still create Address object with the string in map field
    // This allows the user to see and edit the address in the LocationModal
    const createFallbackAddress = (): Address => {
      const address = new Address();
      address.map = trimmedAddress;
      return address;
    };

    try {
      const geocodeResult = await fetchGeocodeData(trimmedAddress);
      if (geocodeResult && geocodeResult.addressComponents) {
        const { streetLocality, country, province, city, pinCode } =
          fetchAddressFromGeoCode(geocodeResult.addressComponents);

        const address = new Address();
        address.street = streetLocality?.trim() || '';
        address.country = country?.long_name || '';
        address.countryCode = country?.short_name || '';
        address.province = province?.long_name || '';
        address.provinceCode = province?.short_name || '';
        address.city = city?.long_name || '';
        address.postalCode = pinCode?.long_name || '';
        address.latitude = geocodeResult.latLng?.lat()?.toString() || '';
        address.longitude = geocodeResult.latLng?.lng()?.toString() || '';
        address.map = geocodeResult.formattedAddress || trimmedAddress;

        return address;
      }
    } catch (error) {
      // Silently fail - return fallback address so user can still edit
      console.warn('Failed to geocode address string, using fallback:', trimmedAddress);
    }

    // Return fallback address with string in map field
    return createFallbackAddress();
  };

  const toggleOriginModal = async (idx: number) => {
    if (idx === index) {
      // If we have a string address but no Address object, try to geocode it
      const originString = watch(`distanceBasedTravel.${index}.origin`);
      const originAddress = watch(`distanceBasedTravel.${index}.originAddress`);

      if (originString && !originAddress) {
        const geocodedAddress = await geocodeStringAddress(originString);
        if (geocodedAddress) {
          setValue(`distanceBasedTravel.${index}.originAddress`, geocodedAddress);
        }
      }

      setOpenOriginModal(prev => !prev);
    }
  };

  const toggleDestinationModal = async (idx: number) => {
    if (idx === index) {
      // If we have a string address but no Address object, try to geocode it
      const destinationString = watch(`distanceBasedTravel.${index}.destination`);
      const destinationAddress = watch(`distanceBasedTravel.${index}.destinationAddress`);

      if (destinationString && !destinationAddress) {
        const geocodedAddress = await geocodeStringAddress(destinationString);
        if (geocodedAddress) {
          setValue(`distanceBasedTravel.${index}.destinationAddress`, geocodedAddress);
        }
      }

      setOpenDestinationModal(prev => !prev);
    }
  };

  const toggleSpendLocationModal = async (idx: number) => {
    if (idx === index) {
      // If we have a string address but no Address object, try to geocode it
      const spendLocationString = watch(`spendBasedTravel.${index}.spendLocation`);
      const spendLocationAddress = watch(`spendBasedTravel.${index}.spendLocationAddress`);

      if (spendLocationString && !spendLocationAddress) {
        const geocodedAddress = await geocodeStringAddress(spendLocationString);
        if (geocodedAddress) {
          setValue(`spendBasedTravel.${index}.spendLocationAddress`, geocodedAddress);
        }
      }

      setOpenSpendLocationModal(prev => !prev);
    }
  };

  const toggleHotelLocationModal = async (idx: number) => {
    if (idx === index) {
      // If we have a string address but no Address object, try to geocode it
      const locationString = watch(`hotelStays.${index}.location`);
      const locationAddress = watch(`hotelStays.${index}.locationAddress`);

      if (locationString && !locationAddress) {
        const geocodedAddress = await geocodeStringAddress(locationString);
        if (geocodedAddress) {
          setValue(`hotelStays.${index}.locationAddress`, geocodedAddress);
        }
      }

      setOpenHotelLocationModal(prev => !prev);
    }
  };

  const handleAddOriginAddress = (address: Address) => {
    setValue(`distanceBasedTravel.${index}.originAddress`, address);
    const label = generateAddressLabel(address);
    setValue(`distanceBasedTravel.${index}.origin`, label, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setOpenOriginModal(false);
  };

  const handleAddDestinationAddress = (address: Address) => {
    setValue(`distanceBasedTravel.${index}.destinationAddress`, address);
    const label = generateAddressLabel(address);
    setValue(`distanceBasedTravel.${index}.destination`, label, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setOpenDestinationModal(false);
  };

  const handleAddSpendLocationAddress = (address: Address) => {
    setValue(`spendBasedTravel.${index}.spendLocationAddress`, address);
    const label = generateAddressLabel(address);
    setValue(`spendBasedTravel.${index}.spendLocation`, label, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setOpenSpendLocationModal(false);
  };

  const handleAddHotelLocationAddress = (address: Address) => {
    setValue(`hotelStays.${index}.locationAddress`, address);
    const label = generateAddressLabel(address);
    setValue(`hotelStays.${index}.location`, label, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setOpenHotelLocationModal(false);
  };

  if (type === 'distance') {
    return (
      <div key={`distance-${index}`}>
        <FieldsRow
          {...getDistanceBasedFieldsData({
            index,
            remove,
            arrayLength: distanceBasedTravel?.length,
            handleChange,
            travelMode: distanceBasedTravel?.[index]?.travelMode,
            toggleOriginModal,
            toggleDestinationModal,
          })}
        />
        <LocationModal
          showModal={openOriginModal}
          onCancel={() => setOpenOriginModal(false)}
          handleSubmit={handleAddOriginAddress}
          initialValue={watch(`distanceBasedTravel.${index}.originAddress`) ?? undefined}
        />
        <LocationModal
          showModal={openDestinationModal}
          onCancel={() => setOpenDestinationModal(false)}
          handleSubmit={handleAddDestinationAddress}
          initialValue={watch(`distanceBasedTravel.${index}.destinationAddress`) ?? undefined}
        />
      </div>
    );
  }

  if (type === 'spend') {
    return (
      <div key={`spend-${index}`}>
        <FieldsRow
          {...getSpendBasedFieldsData({
            index,
            remove,
            arrayLength: spendBasedTravel?.length,
            handleChange,
            toggleSpendLocationModal,
          })}
        />
        <LocationModal
          showModal={openSpendLocationModal}
          onCancel={() => setOpenSpendLocationModal(false)}
          handleSubmit={handleAddSpendLocationAddress}
          initialValue={watch(`spendBasedTravel.${index}.spendLocationAddress`) ?? undefined}
        />
      </div>
    );
  }

  if (type === 'hotel') {
    return (
      <div key={`hotel-${index}`}>
        <FieldsRow
          {...getHotelStayFieldsData({
            index,
            remove,
            arrayLength: hotelStays?.length,
            handleChange,
            toggleHotelLocationModal,
          })}
        />
        <LocationModal
          showModal={openHotelLocationModal}
          onCancel={() => setOpenHotelLocationModal(false)}
          handleSubmit={handleAddHotelLocationAddress}
          initialValue={watch(`hotelStays.${index}.locationAddress`) ?? undefined}
        />
      </div>
    );
  }

  return null;
};

export default Category6Layout;
